// ─── Constants ────────────────────────────────────────────
export const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
export const MONTHS = ["January","February","March","April","May","June", "July","August","September","October","November","December"];

export const DEFAULT_CLASS = {
  title: "", dayOfWeek: 0, time: "09:00", duration: 60,
  teacher: "", venue: "venue_001", price: 20,
  isHybrid: false,
};

export const EMPTY_PRICING = { perSession: 40, fullDay: 0, fullWorkshop: 200 };

// ─── Helpers ──────────────────────────────────────────────
export function formatTime(t) {
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "pm" : "am";
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12}${ap}` : `${h12}:${String(m).padStart(2,"0")}${ap}`;
}

export function toLocalDateStr(date) {
  return [date.getFullYear(),
    String(date.getMonth()+1).padStart(2,"0"),
    String(date.getDate()).padStart(2,"0")].join("-");
}

export function isEventPast(dateStr, time, duration) {
  const end = new Date(`${dateStr}T${time}`);
  end.setMinutes(end.getMinutes() + duration);
  return end < new Date();
}

export function isWorkshopPast(workshop) {
  const lastDay = workshop.days[workshop.days.length - 1];
  if (!lastDay) return false;
  const lastSession = lastDay.sessions[lastDay.sessions.length - 1];
  if (!lastSession) return new Date(`${lastDay.date}T23:59`) < new Date();
  return isEventPast(lastDay.date, lastSession.time, lastSession.duration);
}

export function getDaysInMonth(y, m) { return new Date(y, m+1, 0).getDate(); }
export function getFirstDay(y, m)    { return new Date(y, m, 1).getDay(); }
export function isSameDay(a, b) {
  return a.getFullYear()===b.getFullYear() &&
         a.getMonth()===b.getMonth() &&
         a.getDate()===b.getDate();
}
export function getWeekDates(date) {
  const start = new Date(date);
  start.setDate(date.getDate() - date.getDay());
  return Array.from({length:7},(_,i)=>{ const d=new Date(start); d.setDate(start.getDate()+i); return d; });
}
export function parseLocalDate(str) { return new Date(str + "T12:00:00"); }

// Spaces left for a capped in-person class. taken = booked + spots held for the waitlist.
export function spaceInfo(evt, taken) {
  if (!evt || !evt.cap || evt.venue === "venue_online" || evt._isPrivate) return null;
  const left = Math.max(0, evt.cap - (taken || 0));
  return { left, full: left === 0, waitlist: !!evt.waitlistEnabled };
}
export function spaceLabel(info, short = false) {
  if (!info) return "";
  if (info.full) return info.waitlist ? (short ? "Full · waitlist" : "Full · waitlist available") : "Full";
  return short ? `${info.left} left` : `${info.left} space${info.left === 1 ? "" : "s"} left`;
}
export function workshopDateRange(w) { return w.days.map(d => d.date).sort(); }
export function workshopsForDate(dateStr, workshops) {
  return workshops.filter(w => w.days.some(d => d.date === dateStr));
}

// ─── Workshop kinds ───────────────────────────────────────
// workshop = one-off event · series = same weekday each week · course = irregular dates
export const KIND_LABELS = { workshop: "Workshop", series: "Series", course: "Course" };
export function workshopKind(w) { return KIND_LABELS[w?.kind] ? w.kind : "workshop"; }

// ─── Labels, teachers, images ─────────────────────────────
export function firstName(name) { return (name || "").trim().split(/\s+/)[0] || ""; }

// Calendar pill label: "Essentially Women · Tricia" (falls back to the subject).
export function pillLabel(evt) {
  if (!evt || evt._isPrivate) return "";
  const name    = (evt.title || "").trim() || (evt.subject || "").trim();
  const teacher = firstName(evt.teacher);
  return teacher ? `${name} · ${teacher}` : name;
}

// Match a class/workshop teacher string to a Teacher record (full name, then first name).
export function findTeacher(teachers, name) {
  const n = (name || "").trim().toLowerCase();
  if (!n || !teachers?.length) return null;
  return teachers.find(t => (t.name || "").trim().toLowerCase() === n)
      || teachers.find(t => firstName(t.name).toLowerCase() === firstName(n).toLowerCase())
      || null;
}

// Stored images are "/uploads/images/…" relative to the studio API, or absolute URLs.
export function resolveImg(api, src) {
  if (!src) return null;
  if (/^(https?:)?\/\//.test(src) || src.startsWith("data:")) return src;
  return `${api}${src.startsWith("/") ? "" : "/"}${src}`;
}
